import React, { useRef, useState } from 'react';
import { Form, Input } from 'formik-antd';
import { Formik, FormikProps } from 'formik';
import { Button, Modal } from 'antd';
import cx from 'classnames';
import * as Yup from 'yup';

import FormItem from 'components/formik/FormItem';
import SubmitButton from 'components/formik/SubmitButton';
import { StageStatusEnum } from 'api/backofficeApiClient';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import styles from './RequestAdditionalInfoModal.module.less';

export interface RequestInfo {
  requestType: StageStatusEnum;
  message: string;
}

type Props = {
  onRequestSent: (requestInfo: RequestInfo) => Promise<void>;
  onClose: () => void;
};

interface FormValues {
  request: string;
}

const validationSchema = Yup.object({
  request: Yup.string()
    .required('Field is required')
    .max(10000, 'Request should be 10000 characters maximum'),
});

export default function RequestAdditionalInfoModal(props: Props) {
  const { onClose, onRequestSent } = props;
  const formRef: any = useRef();
  const [isRequestSaving, setIsRequestSaving] = useState({
    [StageStatusEnum.SoftStop]: false,
    [StageStatusEnum.HardStop]: false,
  });

  const handleSendRequest = async (
    formik: FormikProps<FormValues>,
    requestType: StageStatusEnum,
  ) => {
    if (formik.isSubmitting) return false;

    const { request } = formik.values;

    if (!request) return;

    handleSetIsRequestSaving(requestType, true);
    await onRequestSent({ requestType, message: request });
    handleSetIsRequestSaving(requestType, false);
    onClose();
  };

  const handleSetIsRequestSaving = (
    requestType: StageStatusEnum,
    isSaving: boolean,
  ) =>
    setIsRequestSaving((requests) => ({
      ...requests,
      [requestType]: isSaving,
    }));

  const handleModalCancel = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  return (
    <BaseModal
      title="Request additional information"
      open={true}
      closable={true}
      destroyOnClose
      maxWidth={468}
      onCancel={handleModalCancel}
    >
      <Formik
        initialValues={{ request: '' }}
        onSubmit={() => {}}
        validationSchema={validationSchema}
        innerRef={formRef}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className={cx('modal-scroll-wrapper', styles.wrapper)}>
              <div className={styles.text}>
                Please enter your request below, it will be sent as the body of
                the email.
              </div>

              <FormItem data-id="requestField" label="Request *" name="request">
                <Input.TextArea
                  name="request"
                  placeholder="Enter your request"
                  autoSize={{ minRows: 8, maxRows: 8 }}
                />
              </FormItem>
            </div>

            <BaseModalFooter className="button-space-between">
              <div>
                <Button
                  data-id="closeRequestInfoModalBtn"
                  onClick={handleModalCancel}
                >
                  Cancel
                </Button>
              </div>

              <div>
                <SubmitButton
                  loading={isRequestSaving[StageStatusEnum.SoftStop]}
                  onClick={() =>
                    handleSendRequest(formik, StageStatusEnum.SoftStop)
                  }
                  className="color-warning"
                >
                  <span className="hidden-sm-down">Send as soft stop</span>
                  <span className="hidden-sm-up">Soft stop</span>
                </SubmitButton>

                <SubmitButton
                  loading={isRequestSaving[StageStatusEnum.HardStop]}
                  onClick={() =>
                    handleSendRequest(formik, StageStatusEnum.HardStop)
                  }
                  type="primary"
                  danger
                >
                  <span className="hidden-sm-down">Send as hard stop</span>
                  <span className="hidden-sm-up">Hard stop</span>
                </SubmitButton>
              </div>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
